export const PCM_SAMPLE_RATE = 16000;
const BYTES_PER_SAMPLE = 2;
const WAV_HEADER_BYTES = 44;

// Short enough that text lands every few seconds, long enough that the
// transcriber still has sentence context to work with.
export const CHUNK_MIN_MS = 5000;
export const CHUNK_MAX_MS = 10000;
const SILENCE_CUT_MS = 300;
// A trailing fragment shorter than this carries no usable speech.
const MIN_TAIL_MS = 250;

// Frames are judged against a floor learned from the room rather than a fixed
// level: gain control is off, so an absolute threshold is wrong for most mics.
const NOISE_FLOOR_RATIO = 2.5;
const NOISE_FLOOR_MARGIN = 100;
const NOISE_FLOOR_DRIFT = 1.001;
const LOUD_RMS = 1000;

const samplesToMs = (samples: number) => (samples / PCM_SAMPLE_RATE) * 1000;

const frameRms = (frame: Int16Array) => {
  if (frame.length === 0) {
    return 0;
  }
  let sumOfSquares = 0;
  for (const sample of frame) {
    sumOfSquares += sample * sample;
  }
  return Math.sqrt(sumOfSquares / frame.length);
};

export function encodeWav(frames: Int16Array[], totalSamples: number): Blob {
  const dataBytes = totalSamples * BYTES_PER_SAMPLE;
  const buffer = new ArrayBuffer(WAV_HEADER_BYTES + dataBytes);
  const view = new DataView(buffer);
  const writeAscii = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) {
      view.setUint8(offset + i, text.charCodeAt(i));
    }
  };

  writeAscii(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes, true);
  writeAscii(8, 'WAVE');
  writeAscii(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, PCM_SAMPLE_RATE, true);
  view.setUint32(28, PCM_SAMPLE_RATE * BYTES_PER_SAMPLE, true);
  view.setUint16(32, BYTES_PER_SAMPLE, true);
  view.setUint16(34, 16, true);
  writeAscii(36, 'data');
  view.setUint32(40, dataBytes, true);

  let offset = WAV_HEADER_BYTES;
  for (const frame of frames) {
    for (const sample of frame) {
      view.setInt16(offset, sample, true);
      offset += BYTES_PER_SAMPLE;
    }
  }
  return new Blob([buffer], { type: 'audio/wav' });
}

/** A dropped chunk that carried signal above the margin but never cleared the speech threshold. */
export interface DiscardedChunk {
  durationMs: number;
  peakRms: number;
  noiseFloor: number;
}

/**
 * Cuts a 16 kHz Int16 PCM stream into self-contained WAV chunks. A chunk closes
 * on the first pause after `CHUNK_MIN_MS`, or hard at `CHUNK_MAX_MS`: the
 * transcriber gets no context across chunks, so a cut mid-word costs that word.
 *
 * One instance per dictation run: the learned noise floor only ever falls, so
 * a floor from a quiet room would otherwise read the next run's silence as
 * speech.
 */
export class PcmChunker {
  private frames: Int16Array[] = [];
  private samples = 0;
  private silentSamples = 0;
  private hasSpeech = false;
  private peakRms = 0;
  private noiseFloor = Number.POSITIVE_INFINITY;

  constructor(private readonly onDiscard?: (chunk: DiscardedChunk) => void) {}

  push(pcm: ArrayBuffer): Blob | null {
    const frame = new Int16Array(pcm);
    this.frames.push(frame);
    this.samples += frame.length;

    const rms = frameRms(frame);
    this.peakRms = Math.max(this.peakRms, rms);
    // An all-zero frame (muted track, device gap) would pin the floor at 0 and
    // the threshold at the bare margin for the rest of the run.
    if (rms > 0) {
      this.noiseFloor = Math.min(this.noiseFloor * NOISE_FLOOR_DRIFT, rms);
    }
    const isSilent = rms < this.noiseFloor * NOISE_FLOOR_RATIO + NOISE_FLOOR_MARGIN;
    this.silentSamples = isSilent ? this.silentSamples + frame.length : 0;
    // The floor starts at the first frame's level, so a run that opens on
    // speech reads as silent until the first pause; the absolute level keeps
    // that chunk from being dropped.
    if (!isSilent || rms >= LOUD_RMS) {
      this.hasSpeech = true;
    }

    const durationMs = samplesToMs(this.samples);
    const isPaused = samplesToMs(this.silentSamples) >= SILENCE_CUT_MS;
    if (durationMs >= CHUNK_MAX_MS || (durationMs >= CHUNK_MIN_MS && isPaused)) {
      return this.take();
    }
    return null;
  }

  flush(): Blob | null {
    if (samplesToMs(this.samples) < MIN_TAIL_MS) {
      this.reset();
      return null;
    }
    return this.take();
  }

  // A chunk with no speech is dropped, not sent: the transcriber answers
  // silence with invented text. Dead silence is the user pausing; a dropped
  // chunk that still carried signal above the margin may be a mic sitting
  // under the threshold, which is worth knowing about.
  private take(): Blob | null {
    const durationMs = samplesToMs(this.samples);
    const chunk = this.hasSpeech ? encodeWav(this.frames, this.samples) : null;
    if (!chunk && this.peakRms >= NOISE_FLOOR_MARGIN) {
      this.onDiscard?.({
        durationMs,
        peakRms: Math.round(this.peakRms),
        noiseFloor: Number.isFinite(this.noiseFloor) ? Math.round(this.noiseFloor) : 0,
      });
    }
    this.reset();
    return chunk;
  }

  private reset() {
    this.frames = [];
    this.samples = 0;
    this.silentSamples = 0;
    this.hasSpeech = false;
    this.peakRms = 0;
  }
}
